/* oxlint-disable no-console */
import { datadog } from '@flipster/utils/datadog';
import deferred from '@flipster/utils/deferred';

import type { AbstractSocketEvents, SocketState } from './AbstractSocket';
import AutoReconnectingSocket from './AutoReconnectingSocket';
import { WebSocketError } from './misc/errors/web-socket-error';
import { MessageEvent } from './r230522/types';

export interface SocketPluginClass {
  new (socket: Socket, sendRaw: (data: string) => void): SocketPlugin;
}
export interface SocketPlugin {
  handleRawMessageEvent?(messageEvent: MessageEvent, next: (messageEvent: MessageEvent) => void): void;
}
export interface SocketEvents extends AbstractSocketEvents {
  sessionExpired: void;
}

class Socket<E extends SocketEvents = SocketEvents> extends AutoReconnectingSocket<unknown, E> {
  #plugins: SocketPlugin[] = [];
  #socket: WebSocket | null = null;
  #messageQueue: string[] = [];

  constructor(
    private readonly url: string | URL,
    private readonly socketName = 'socket',
    plugins: SocketPluginClass[] = [],
  ) {
    super();
    this.#plugins = plugins.map((Plugin) => new Plugin(this, this.#sendRaw));
    this.on('error', (err) => {
      datadog.addError(err);
      if (this.state === 'closed') return;
      console.error(`[${this.debugLabel}] - Error occured: closing socket...`);
    });
    this.on('stateChanged', (state) => {
      console.info(`[${this.debugLabel}] - State changed: ${state}`);
    });
  }

  get debugLabel(): string {
    return this.socketName;
  }

  get state(): SocketState {
    if (!this.#socket) return 'closed';
    switch (this.#socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      default:
        return 'closed';
    }
  }

  public tryConnect() {
    const lastState = this.state;
    if (lastState !== 'closed') return super.tryConnect();
    const connected = deferred<void>();
    const socket = new WebSocket(this.url);
    this.#socket = socket;
    this.#emitStateChanged(lastState);
    const ensureSingleton = () => {
      const singleton = this.#socket === socket;
      if (!singleton) kill(socket);
      return singleton;
    };
    const onerror = (err: Error) => {
      if (!ensureSingleton()) return;
      // error 이벤트가 소켓이 닫혀있는 중에 날 리 없다고 가정.
      // (만약 소켓이 닫혀있는 중에 error 이벤트가 발생하면 closed 이벤트가 중복으로 emit될 수 있음)
      // 어차피 oldState가 'closed'만 아니기만 하면 아래 코드를 통해
      // stateChanged 이벤트가 emit 되므로 적당히 'connected'를 oldState로 사용함.
      this.#close('connected');
      connected.reject(err);
      this.emit('error', err);
    };
    socket.onopen = () => {
      if (!ensureSingleton()) return;
      this.#emitStateChanged();
      connected.resolve();
    };
    socket.onmessage = (event: MessageEvent) => {
      if (!ensureSingleton()) return;
      type Next = (message: MessageEvent) => void;
      this.#plugins.reduceRight<Next>(
        (next, plugin) => {
          if (!plugin.handleRawMessageEvent) return next;
          return (e) => plugin.handleRawMessageEvent!(e, next);
        },
        (e) => this.emit('message', JSON.parse(e.data)),
      )(event);
    };
    socket.onclose = (e) => {
      if (!ensureSingleton()) return;
      connected.reject(new Error('Socket closed'));
      // https://prextech.slack.com/archives/C04N8SLNS1F/p1754969285645119
      // 서버에서 1013 code로 닫은 경우 자동 재연결 시도하지 않도록 함.
      // 이 시점에는 이미 this.state === 'closed'이므로 this.close()를 불러봤자 작동하지 않음.
      // 따라서 super.close()를 직접 호출함.
      // 2025-09-18 시점 safari에서는 1012 이상의 close code가 제대로 처리되지 않는 문제가 있음.
      // 따라서 1008을 사용하도록 함.
      if (e.code === 1013 || e.code === 1008) {
        super.close();
        this.emit('sessionExpired', undefined);
      }
      this.#close('connected'); // onerror와 같은 이유로 'connected'를 oldState로 사용함.
    };
    socket.onerror = (e) => onerror(new WebSocketError(e));
    const timeoutId = setTimeout(() => {
      if (socket.readyState !== WebSocket.CONNECTING) return;
      onerror(new Error('Connection timeout'));
    }, 10000);
    return connected.then(() => clearTimeout(timeoutId));
  }

  public close() {
    const lastState = this.state;
    if (lastState === 'closed') return;
    super.close();
    this.#close();
  }

  public send(data: unknown) {
    this.#sendRaw(JSON.stringify(data));
  }

  protected clearQueuedMessages() {
    this.#messageQueue = [];
  }

  protected flushQueuedMessages() {
    if (this.state !== 'connected') return;
    if (this.#messageQueue.length === 0) return;
    this.#messageQueue.forEach((data) => this.#sendRaw(data));
    this.clearQueuedMessages();
  }

  // private
  #sendRaw = (data: string) => {
    if (this.state === 'connected') this.#socket?.send(data);
    else this.#messageQueue.push(data);
  };

  #emitStateChanged = (oldState?: SocketState) => {
    if (oldState === this.state) return;
    this.emit('stateChanged', this.state);
  };

  #close = (oldState: SocketState = this.state) => {
    if (this.#socket) kill(this.#socket);
    this.#socket = null;
    this.#emitStateChanged(oldState);
  };
}

export default Socket;

function kill(socket: WebSocket) {
  /* oxlint-disable no-param-reassign */
  // 연결중이었다면 열리자마자 닫도록 함.
  if (socket.readyState === WebSocket.CONNECTING) {
    socket.onopen = () => {
      socket.onopen = null;
      socket.close();
    };
  } else {
    socket.onopen = null;
    // 열려있었으면 닫음
    if (socket.readyState === WebSocket.OPEN) socket.close();
  }
  // 나머지 핸들러 정리
  socket.onmessage = null;
  socket.onclose = null;
  socket.onerror = null;
  /* eslint-enable no-param-reassign */
}
